import { CatalogItemPriceBase } from "@common/types/catalogItem";

// Callers must never treat "the first price" as "the current price". These
// helpers pick the default active row when one is set, otherwise the most
// recently created active row (UUIDv7 id as a stable createdAt tiebreaker).
export function compareByRecencyDesc(
    a: CatalogItemPriceBase,
    b: CatalogItemPriceBase,
): number {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();
    if (aTime !== bTime) {
        return bTime - aTime;
    }
    if (a.id === b.id) {
        return 0;
    }
    return a.id > b.id ? -1 : 1;
}

function pickDefaultOrLatest(
    prices: CatalogItemPriceBase[],
): CatalogItemPriceBase | null {
    const defaultPrice = prices.find((price) => price.isDefault);
    if (defaultPrice) {
        return defaultPrice;
    }
    return [...prices].sort(compareByRecencyDesc)[0] ?? null;
}

export function getCurrentActivePrice(
    prices: CatalogItemPriceBase[] | undefined,
): CatalogItemPriceBase | null {
    return pickDefaultOrLatest((prices ?? []).filter((price) => price.active));
}

export function getActivePricesForCurrency(
    prices: CatalogItemPriceBase[] | undefined,
    currency: string,
): CatalogItemPriceBase[] {
    return (prices ?? [])
        .filter((price) => price.active && price.currency === currency)
        .sort((left, right) => {
            if (left.isDefault !== right.isDefault) {
                return left.isDefault ? -1 : 1;
            }
            return compareByRecencyDesc(left, right);
        });
}

export function getCurrentActivePriceForCurrency(
    prices: CatalogItemPriceBase[] | undefined,
    currency: string,
): CatalogItemPriceBase | null {
    return pickDefaultOrLatest(getActivePricesForCurrency(prices, currency));
}

// The current active price per currency, keeping the most recent when several
// active rows share a currency. Used when copying an item so the copy never
// inherits duplicate ambiguous active prices.
export function getCurrentActivePricesByCurrency(
    prices: CatalogItemPriceBase[] | undefined,
): CatalogItemPriceBase[] {
    const latestByCurrency = new Map<string, CatalogItemPriceBase>();
    const activePricesByRecency = (prices ?? [])
        .filter((price) => price.active)
        .sort(compareByRecencyDesc);
    for (const price of activePricesByRecency) {
        if (!latestByCurrency.has(price.currency)) {
            latestByCurrency.set(price.currency, price);
        }
    }
    return [...latestByCurrency.values()];
}
